import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Printer, Wallet } from "lucide-react";
import {
  examFeeApi,
  examFeeQueryKeys,
  examFeeStatusLabel,
  EXAM_FEE_LEGACY_PAY_METHOD,
  examFeePayMethodLabel,
  bhytCareContextLabel,
  BHYT_CARE_CONTEXTS,
  type BhytCareContext,
  type ExamFee,
  type ExamFeePayMethod,
} from "../../../shared/api/examFee";
import { apiError } from "../../../shared/api/client";
import { formatDateTime, formatVndWithUnit, paymentMethodLabel } from "../../../shared/format";
import type { PaymentMethod } from "../../../shared/types/billing";
import { escapeHtml } from "../../../shared/escapeHtml";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

const PAY_METHODS: ExamFeePayMethod[] = ["CASH", "BANK_TRANSFER", "CARD"];

/** Map an exam-fee status to a Badge variant while keeping the same label semantics. */
function feeStatusVariant(status: ExamFee["status"]): BadgeProps["variant"] {
  switch (status) {
    case "PAID":
      return "success";
    case "PENDING":
      return "warning";
    case "WAIVED":
    case "BHYT":
    case "BHYT_UNPRICED":
      return "secondary";
    default:
      return "muted";
  }
}

/** Human label for whatever the backend stored in `paymentMethod` (may be a billing method or a fee method). */
function paymentMethodDisplay(stored: string | null): string {
  if (!stored) return "—";
  const raw = EXAM_FEE_LEGACY_PAY_METHOD[stored] ?? stored;
  if (raw in examFeePayMethodLabel) {
    return examFeePayMethodLabel[raw as ExamFeePayMethod];
  }
  if (raw in paymentMethodLabel) {
    return paymentMethodLabel[raw as PaymentMethod];
  }
  return raw;
}

/** A benefit rate, or a dash when it is genuinely unknown — never a 0% standing in for "unknown". */
function percentText(pct: number | null): string {
  if (pct === null || pct === undefined) return "Chưa xác định";
  // 80.00 → "80%", 47.50 → "47,5%" — the half-percent is real and must not be rounded off screen.
  const s = Number(pct).toFixed(2).replace(/\.?0+$/, "");
  return `${s.replace(".", ",")}%`;
}

/** Whether the applied rate is below the card's — the case worth drawing the eye to. */
function isReduced(fee: ExamFee): boolean {
  return (
    fee.benefitPercent !== null &&
    fee.baseBenefitPercent !== null &&
    fee.benefitPercent < fee.baseBenefitPercent
  );
}

/** Amount line respecting the fee status (WAIVED / BHYT render as words, not a number). */
function amountText(fee: ExamFee): string {
  if (fee.status === "WAIVED") return "Miễn phí";
  if (fee.status === "BHYT") return "BHYT chi trả";
  // #19854 — a voided fee is not money the patient owes; printing the bare number said it was.
  if (fee.status === "VOIDED") return `${formatVndWithUnit(fee.amount)} — đã huỷ, không thu`;
  if (fee.status === "COVERED") return "Đã thu ở lượt khám trước";
  // #18834 records the GROSS fee under BHYT_UNPRICED so it stops being an invisible loss — but
  // nothing is collected at the counter. Printing the bare number here made it read as an amount
  // the patient owes, which is the opposite of what the status means.
  if (fee.status === "BHYT_UNPRICED") {
    return `${formatVndWithUnit(fee.amount)} — chưa thu tại quầy`;
  }
  return formatVndWithUnit(fee.amount);
}

/**
 * Client-side A5 receipt. Opens a new window with formatted HTML and prints.
 * Kept self-contained (inline styles) so the print window needs no app CSS.
 */
function printReceipt(params: {
  fee: ExamFee;
  serviceName: string;
  patientName: string;
  patientCode: string | null;
  collector: string;
}) {
  const { fee, serviceName, patientName, patientCode, collector } = params;
  const now = new Date();
  const rows: [string, string][] = [
    ["Bệnh nhân", patientName],
    ["Mã BN", patientCode ?? "—"],
    ["Dịch vụ", serviceName],
    ["Số tiền", amountText(fee)],
    ["Hình thức thanh toán", paymentMethodDisplay(fee.paymentMethod)],
    ["Ngày", formatDateTime(fee.paidAt ?? now.toISOString())],
    ["Người thu", collector],
  ];

  const html = `<!doctype html>
<html lang="vi"><head><meta charset="utf-8" />
<title>Phiếu thu phí khám</title>
<style>
  @page { size: A5; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111; margin: 0; }
  h1 { font-size: 18px; text-align: center; margin: 0 0 4px; text-transform: uppercase; letter-spacing: 0.5px; }
  .sub { text-align: center; color: #555; font-size: 12px; margin-bottom: 20px; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  td { padding: 7px 0; vertical-align: top; }
  td.k { color: #555; width: 42%; }
  td.v { font-weight: 600; text-align: right; }
  .amount td.v { font-size: 18px; color: #166534; }
  .sign { display: flex; justify-content: space-between; margin-top: 48px; font-size: 13px; text-align: center; }
  .sign div { width: 45%; }
  .sign .role { font-weight: 600; }
  .sign .hint { color: #777; font-size: 11px; font-style: italic; }
  .sign .line { margin-top: 56px; border-top: 1px dashed #999; }
</style></head>
<body>
  <h1>Phiếu thu phí khám</h1>
  <div class="sub">Ngày ${escapeHtml(formatDateTime(fee.paidAt ?? now.toISOString()))}</div>
  <table>
    ${rows
      .map(
        ([k, v], i) =>
          `<tr class="${i === 3 ? "amount" : ""}"><td class="k">${escapeHtml(k)}</td><td class="v">${escapeHtml(v)}</td></tr>`
      )
      .join("")}
  </table>
  <div class="sign">
    <div><span class="role">Người nộp tiền</span><div class="hint">(Ký, ghi rõ họ tên)</div><div class="line"></div></div>
    <div><span class="role">Người thu tiền</span><div class="hint">(Ký, ghi rõ họ tên)</div><div class="line"></div></div>
  </div>
  <script>window.onload = function () { window.print(); };</script>
</body></html>`;

  const w = window.open("", "_blank", "width=640,height=800");
  if (!w) {
    alert("Trình duyệt đã chặn cửa sổ in. Vui lòng cho phép pop-up để in phiếu thu.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export function ExamFeeCard({
  appointmentId,
  patientName,
  patientCode,
  collectorName,
  serviceName,
}: {
  appointmentId: string;
  patientName: string;
  patientCode: string | null;
  /** Name of the logged-in user collecting the fee (printed on the receipt). */
  collectorName: string;
  /** Service name from the appointment — shown on the receipt when the fee
   *  record itself carries none (#18562), instead of the generic "Phí khám". */
  serviceName?: string | null;
}) {
  const qc = useQueryClient();
  const [pickingMethod, setPickingMethod] = useState(false);

  const feeQuery = useQuery({
    queryKey: examFeeQueryKeys.detail(appointmentId),
    queryFn: () => examFeeApi.get(appointmentId),
  });

  const pay = useMutation({
    mutationFn: (method: ExamFeePayMethod) => examFeeApi.pay(appointmentId, method),
    onSuccess: () => {
      setPickingMethod(false);
      qc.invalidateQueries({ queryKey: examFeeQueryKeys.detail(appointmentId) });
    },
  });

  const setContext = useMutation({
    mutationFn: (c: BhytCareContext) => examFeeApi.setCareContext(appointmentId, c),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: examFeeQueryKeys.detail(appointmentId) });
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-base">Phí khám</CardTitle>
        {feeQuery.data && (
          <Badge variant={feeStatusVariant(feeQuery.data.status)}>
            {examFeeStatusLabel[feeQuery.data.status]}
          </Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {feeQuery.isLoading && <Skeleton className="h-[72px] w-full" />}

        {feeQuery.isError && (
          <p className="m-0 text-sm text-destructive">
            Không tải được phí khám. {apiError(feeQuery.error).detail}
          </p>
        )}

        {feeQuery.data && (
          <>
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">Số tiền</dt>
                <dd className="font-semibold text-foreground">{amountText(feeQuery.data)}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">Dịch vụ</dt>
                <dd className="text-foreground">
                  {feeQuery.data.serviceName ?? serviceName ?? "Phí khám"}
                </dd>
              </div>
              {/* #18857 — the card's rate and the rate we applied, side by side. They are the
                  same most of the time, and showing both anyway is what makes the times they
                  differ legible instead of surprising. */}
              {feeQuery.data.baseBenefitPercent !== null && (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-muted-foreground">Mức hưởng theo thẻ</dt>
                    <dd className="text-foreground">
                      {percentText(feeQuery.data.baseBenefitPercent)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-muted-foreground">Mức hưởng áp dụng</dt>
                    <dd
                      className={
                        isReduced(feeQuery.data)
                          ? "font-semibold text-amber-700 dark:text-amber-500"
                          : "text-foreground"
                      }
                    >
                      {percentText(feeQuery.data.benefitPercent)}
                    </dd>
                  </div>
                </>
              )}
              {feeQuery.data.careContext && (
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-muted-foreground">Bối cảnh khám</dt>
                  <dd className="text-foreground">
                    {bhytCareContextLabel[feeQuery.data.careContext]}
                  </dd>
                </div>
              )}
              {feeQuery.data.status === "PAID" && (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-muted-foreground">Hình thức</dt>
                    <dd className="text-foreground">
                      {paymentMethodDisplay(feeQuery.data.paymentMethod)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-muted-foreground">Thời điểm thu</dt>
                    <dd className="text-foreground">{formatDateTime(feeQuery.data.paidAt)}</dd>
                  </div>
                </>
              )}
            </dl>

            {/* #18857 — the fee is unpriced and the missing piece is the care context. Ask for
                the one thing that is missing, right where the consequence is visible, instead of
                sending the user to a settings page to discover which field it was. */}
            {feeQuery.data.status === "BHYT_UNPRICED" && !feeQuery.data.careContext && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
                <h3 className="mt-0 mb-1 text-sm font-medium text-foreground">
                  Chưa xác định mức hưởng BHYT
                </h3>
                <p className="mb-3 mt-0 text-sm text-muted-foreground">
                  Mức hưởng phụ thuộc vào việc bệnh nhân đến khám ở đâu. Chọn bối cảnh khám để hệ
                  thống tính phần bệnh nhân đồng chi trả.
                </p>
                <div className="flex flex-wrap gap-2">
                  {BHYT_CARE_CONTEXTS.map((c) => (
                    <Button
                      key={c}
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={setContext.isPending}
                      onClick={() => setContext.mutate(c)}
                    >
                      {bhytCareContextLabel[c]}
                    </Button>
                  ))}
                </div>
                {setContext.isError && (
                  <p className="mb-0 mt-2 text-sm text-destructive">
                    {apiError(setContext.error).detail}
                  </p>
                )}
              </div>
            )}

            {/* Recorded, still unpriced — so the context was not what was missing. Say which of
                the other two it is rather than leaving the user pressing the same buttons. */}
            {feeQuery.data.status === "BHYT_UNPRICED" && feeQuery.data.careContext && (
              <p className="mb-0 mt-1 text-sm text-muted-foreground">
                {feeQuery.data.careContext === "CORRECT_ROUTE"
                  ? "Chưa tính được: dịch vụ khám chưa có mức giá BHYT."
                  : "Chưa tính được tự động: mức hưởng cho trường hợp này phải được xác nhận thủ công theo quy định BHYT."}
              </p>
            )}

            {feeQuery.data.status === "PENDING" && !pickingMethod && (
              <div className="pt-1">
                <Button type="button" onClick={() => setPickingMethod(true)}>
                  <Wallet />
                  Thu phí khám
                </Button>
              </div>
            )}

            {feeQuery.data.status === "PENDING" && pickingMethod && (
              <div className="mt-2 rounded-md bg-muted p-4">
                <h3 className="mt-0 mb-3 text-sm font-medium text-foreground">
                  Hình thức thanh toán
                </h3>
                <div className="flex flex-wrap gap-2">
                  {PAY_METHODS.map((m) => (
                    <Button
                      key={m}
                      type="button"
                      variant="secondary"
                      disabled={pay.isPending}
                      onClick={() => pay.mutate(m)}
                    >
                      {examFeePayMethodLabel[m]}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={pay.isPending}
                    onClick={() => setPickingMethod(false)}
                  >
                    Hủy
                  </Button>
                </div>
                {pay.isPending && (
                  <p className="mb-0 mt-2 text-sm text-muted-foreground">Đang thu…</p>
                )}
                {pay.isError && (
                  <p className="mb-0 mt-2 text-sm text-destructive">{apiError(pay.error).detail}</p>
                )}
              </div>
            )}

            {feeQuery.data.status !== "PENDING" && (
              <>
                <Separator />
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      printReceipt({
                        fee: feeQuery.data,
                        serviceName: feeQuery.data.serviceName ?? serviceName ?? "Phí khám",
                        patientName,
                        patientCode,
                        collector: collectorName,
                      })
                    }
                  >
                    <Printer />
                    In phiếu thu
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
